import { FormatData } from '@classes/format-data';
import { Order, LogElement } from '@models/order';
import { clearCopiedSuperbill } from '@classes/superbill';

export class RepairJob {
  static nextFxInvoice(originalInvoice: string, orders: Order[]): string {
    const root = FormatData.invoiceFamilyRoot(originalInvoice);
    if (!root) {
      return '';
    }
    const escaped = root.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-FX(\\d+)?$`, 'i');
    let max = 0;
    (orders || []).forEach((order) => {
      const invoice = FormatData.displayInvoiceNumber(order?.invoiceNumber);
      const match = invoice.match(pattern);
      if (!match) {
        return;
      }
      const n = match[1] ? parseInt(match[1], 10) : 1;
      if (!isNaN(n) && n > max) {
        max = n;
      }
    });
    if (max === 0) {
      return `${root}-FX`;
    }
    return `${root}-FX${max + 1}`;
  }

  static applyRepairMetadata(target: any, repairItems: string[]): void {
    const items = Array.isArray(repairItems) ? repairItems.filter(Boolean) : [];
    target.status = 'TO REPAIR';
    target.todo = 'REPAIR';
    target.jobAction = 'REPAIR';
    target.jobActions = ['REPAIR'];
    (target.products || []).forEach((productElement: any) => {
      if (!productElement.frame) {
        productElement.frame = {};
      }
      productElement.frame.pof = true;
      productElement.frame.physicalStatus = 'ENCLOSED';
      productElement.frame.preOrderStatus = 'ENCLOSED';
      productElement.frame.enclosed = true;
      productElement.frame.repairItems = items;
      productElement.frame.activity = [
        {
          date: new Date(),
          status: 'REPAIR / ENCLOSED',
          referenceNumber: '',
          carrier: '',
          trackingNumber: '',
          labVendor: '',
        },
      ];
      productElement.frame.activityStatus = 'REPAIR / ENCLOSED';
    });
  }

  static buildLinkedRepairOrder(
    original: any,
    originalInvoice: string,
    invoiceNumber: string,
    repairItems: string[],
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    }
  ): Order {
    const clone = JSON.parse(JSON.stringify(original || {}));
    clearCopiedSuperbill(clone);
    clone._id = '';
    clone.invoiceNumber = invoiceNumber;
    clone.linkedInvoice = originalInvoice;
    clone.date = new Date();
    clone.dateAdded = undefined;
    clone.dateUpdated = undefined;
    clone.notes = '';
    clone.vendorOrder = null;
    clone.rxStatus = '';
    clone.frameOnly = false;
    clone.patient = context.patientId;
    clone.user = context.userId;
    clone.store = context.store;
    delete clone.initials;

    (clone.products || []).forEach((productElement: any) => {
      productElement._id = null;
      productElement.retail = 0;
      productElement.tax = 0;
      productElement.custom = 0;
      productElement.shipping = 0;
      productElement.subtotal = 0;
    });

    RepairJob.applyRepairMetadata(clone, repairItems);

    const itemsLabel = repairItems.filter(Boolean).join(', ');
    const created = new LogElement();
    created.initials = context.initials;
    created.user = context.userId;
    created.item = 'JOB CREATED';
    created.notes = '';
    created.date = Date.now().toString();

    const repairLog = new LogElement();
    repairLog.initials = context.initials;
    repairLog.user = context.userId;
    repairLog.item = originalInvoice
      ? `REPAIR FROM ${originalInvoice}`
      : 'REPAIR';
    repairLog.notes = itemsLabel ? `WHAT NEEDS REPAIR: ${itemsLabel}` : '';
    repairLog.date = Date.now().toString();
    clone.log = [created, repairLog];

    return clone as Order;
  }
}
